import { useEffect, useState } from 'react';
import { Bell, Mic, MicOff, Volume2 } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { Switch } from '@/components/atoms/switch';
import {
  useCallWaitingTone,
  useMicId,
  useMicPermission,
  useSpeakerId,
  useVoipActions,
} from '@/stores/voip.store';
import { SYSTEM_DEFAULT_DEVICE_ID } from '@/lib/voip/audioDevices';
import { cn } from '@/lib/utils';

interface Devices {
  mics: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
}

/** Enumerate audio in/out devices. Requests mic permission once so the labels
 *  are populated (otherwise the browser returns empty device labels). */
function useAudioDevices(): Devices {
  const [devices, setDevices] = useState<Devices>({ mics: [], speakers: [] });

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const s = await navigator.mediaDevices.getUserMedia({ audio: true });
        s.getTracks().forEach((t) => t.stop());
      } catch {
        /* permission denied — we still enumerate (labels may be blank) */
      }
      let all: MediaDeviceInfo[] = [];
      try {
        all = await navigator.mediaDevices.enumerateDevices();
      } catch {
        /* not supported */
      }
      if (!active) return;
      setDevices({
        mics: all.filter((d) => d.kind === 'audioinput'),
        speakers: all.filter((d) => d.kind === 'audiooutput'),
      });
    };
    void load();
    navigator.mediaDevices?.addEventListener?.('devicechange', load);
    return () => {
      active = false;
      navigator.mediaDevices?.removeEventListener?.('devicechange', load);
    };
  }, []);

  return devices;
}

const METER_BARS = 12;

/** Live input-level meter for the selected mic (green bars, like Close). Runs
 *  only while mounted (the panel is open) and tears the audio graph down after. */
function MicMeter({ micId }: { micId: string | null }) {
  const [level, setLevel] = useState(0); // 0..1

  useEffect(() => {
    let stream: MediaStream | null = null;
    let ctx: AudioContext | null = null;
    let raf = 0;
    let cancelled = false;

    const start = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: micId ? { deviceId: { exact: micId } } : true,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        const AudioCtx =
          window.AudioContext ??
          (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
        if (!AudioCtx) return;
        ctx = new AudioCtx();
        const source = ctx.createMediaStreamSource(stream);
        const analyser = ctx.createAnalyser();
        analyser.fftSize = 512;
        source.connect(analyser);
        const data = new Uint8Array(analyser.frequencyBinCount);
        const tick = () => {
          analyser.getByteTimeDomainData(data);
          let peak = 0;
          for (let i = 0; i < data.length; i++) {
            peak = Math.max(peak, Math.abs(data[i] - 128) / 128);
          }
          setLevel((prev) => prev * 0.6 + Math.min(1, peak * 1.6) * 0.4);
          raf = requestAnimationFrame(tick);
        };
        tick();
      } catch {
        /* no mic / denied — meter stays flat */
      }
    };
    void start();

    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
      stream?.getTracks().forEach((t) => t.stop());
      void ctx?.close().catch(() => {});
    };
  }, [micId]);

  const litBars = Math.round(level * METER_BARS);
  return (
    <div className="flex items-center gap-[3px]" aria-hidden>
      {Array.from({ length: METER_BARS }).map((_, i) => (
        <span
          key={i}
          className={cn(
            'h-3 w-1.5 rounded-sm transition-colors',
            i < litBars ? (i < METER_BARS * 0.7 ? 'bg-emerald-500' : 'bg-amber-500') : 'bg-muted',
          )}
        />
      ))}
    </div>
  );
}

const SYSTEM_DEFAULT = SYSTEM_DEFAULT_DEVICE_ID;

function deviceLabel(d: MediaDeviceInfo, fallback: string): string {
  return d.label || `${fallback} ${d.deviceId.slice(0, 4)}`;
}

/** The id and the label to remember for a picked device. The label is what
 *  finds the same headset again after it has been unplugged and plugged back
 *  in — ids are regenerated on a re-enumeration. */
function picked(devices: MediaDeviceInfo[], value: string): [string | null, string | null] {
  if (value === SYSTEM_DEFAULT) return [null, null];
  return [value, devices.find((d) => d.deviceId === value)?.label || null];
}

/**
 * "Audio Settings" — microphone + speaker pickers with a live input meter.
 *
 * `layerClassName` lifts the dropdown lists: they portal to <body> on the
 * popover layer, which sits underneath the incoming-call card, so opened from
 * that card they would drop behind it.
 */
export function AudioSettings({ layerClassName }: { layerClassName?: string } = {}) {
  const { mics, speakers } = useAudioDevices();
  const micId = useMicId();
  const speakerId = useSpeakerId();
  const callWaitingTone = useCallWaitingTone();
  const micPermission = useMicPermission();
  const { setMicId, setSpeakerId, setCallWaitingTone, refreshMicPermission } = useVoipActions();

  // Opening this panel is what an agent does after being told the microphone is
  // blocked, and often after going to the browser and unblocking it. Re-read it
  // here so the warning clears itself rather than sitting there until the next
  // sign-in. `useAudioDevices` above has just asked for the microphone, so by
  // now the browser has a fresh answer to give.
  useEffect(() => {
    void refreshMicPermission();
  }, [refreshMicPermission, mics.length]);

  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
        Audio
      </span>

      {/* Without a microphone there is no call at all: the SDK holds the answer
          until it has one, so the caller goes on ringing and the agent sees a
          call that will not connect. Worth saying plainly, and here — this is
          the panel they open when audio is wrong. */}
      {micPermission === 'denied' && (
        <p className="flex items-start gap-2 rounded-md bg-destructive/10 px-2 py-1.5 text-xs text-destructive">
          <MicOff className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>
            Your browser is blocking the microphone, so calls can&apos;t connect. Allow microphone
            access for this site, then reload.
          </span>
        </p>
      )}

      {/* Microphone */}
      <div className="flex items-center gap-2">
        <Mic className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <Select value={micId ?? SYSTEM_DEFAULT} onValueChange={(v) => setMicId(...picked(mics, v))}>
          <SelectTrigger className="h-8 flex-1 text-sm">
            <SelectValue placeholder="System Default" />
          </SelectTrigger>
          <SelectContent className={layerClassName}>
            <SelectItem value={SYSTEM_DEFAULT}>System Default</SelectItem>
            {mics
              .filter((d) => d.deviceId && d.deviceId !== SYSTEM_DEFAULT)
              .map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId}>
                  {deviceLabel(d, 'Microphone')}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Live mic level */}
      <div className="pl-[22px]">
        <MicMeter micId={micId} />
      </div>

      {/* Speaker */}
      <div className="flex items-center gap-2">
        <Volume2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <Select
          value={speakerId ?? SYSTEM_DEFAULT}
          onValueChange={(v) => setSpeakerId(...picked(speakers, v))}
        >
          <SelectTrigger className="h-8 flex-1 text-sm">
            <SelectValue placeholder="System Default" />
          </SelectTrigger>
          <SelectContent className={layerClassName}>
            <SelectItem value={SYSTEM_DEFAULT}>System Default</SelectItem>
            {speakers
              .filter((d) => d.deviceId && d.deviceId !== SYSTEM_DEFAULT)
              .map((d) => (
                <SelectItem key={d.deviceId} value={d.deviceId}>
                  {deviceLabel(d, 'Speaker')}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
      </div>

      {/* Call-waiting tone. Off silences the beep for a second caller only —
          a call arriving when nothing else is up still rings. */}
      <label className="flex items-center gap-2 pt-0.5 text-sm">
        <Bell className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <span className="flex-1">Call waiting tone</span>
        <Switch size="sm" checked={callWaitingTone} onCheckedChange={setCallWaitingTone} />
      </label>
    </div>
  );
}
